import React from 'react';
import { Link } from 'react-router-dom';
import logo from '../../../assets/logo.png';

interface AuthLayoutProps {
  children: React.ReactNode;
  title: string;
  subtitle: string;
  mode?: 'login' | 'register';
}

const CONTACTS = [
  { initials: 'JF', name: 'James Fletcher',   title: 'Owner',               delay: '0ms' },
  { initials: 'SM', name: 'Sarah Mitchell',    title: 'General Manager',     delay: '120ms' },
  { initials: 'TB', name: 'Tom Bradley',       title: 'Operations Director', delay: '240ms' },
];

export default function AuthLayout({ children, title, subtitle, mode = 'login' }: AuthLayoutProps) {
  return (
    <div className="min-h-screen flex flex-col font-sans bg-[#060A12] relative overflow-hidden">

      {/* Background dot grid */}
      <div
        className="pointer-events-none absolute inset-0"
        style={{ backgroundImage: 'radial-gradient(rgba(251,213,67,0.055) 1.5px, transparent 1.5px)', backgroundSize: '28px 28px' }}
      />
      {/* Glow blobs */}
      <div className="pointer-events-none absolute -bottom-32 -left-32 w-[600px] h-[600px] rounded-full bg-brand-500/8 blur-[120px]" />
      <div className="pointer-events-none absolute top-0 right-0 w-[400px] h-[400px] rounded-full bg-slate-800/40 blur-[100px]" />

      {/* ── Top bar ── */}
      <header className="relative z-10 flex items-center justify-between px-6 sm:px-10 py-5">
        <Link to="/">
          <img src={logo} alt="LocalContact" className="h-7 w-auto object-contain" />
        </Link>
        <div className="text-[13px] text-slate-500 font-medium">
          {mode === 'login' ? (
            <>Don't have an account?{' '}
              <Link to="/register" className="text-brand-400 font-bold hover:text-brand-300 transition-colors">Sign up free</Link>
            </>
          ) : (
            <>Already have an account?{' '}
              <Link to="/login" className="text-brand-400 font-bold hover:text-brand-300 transition-colors">Sign in</Link>
            </>
          )}
        </div>
      </header>

      {/* ── Main content ── */}
      <div className="relative z-10 flex-1 flex items-center justify-center px-4 py-8 sm:py-12">
        <div className="w-full max-w-6xl mx-auto flex flex-col lg:flex-row items-center gap-10 lg:gap-16 xl:gap-24">

          {/* ── Left: brand copy + product preview ── */}
          <div className="flex-1 hidden lg:flex flex-col gap-8 max-w-[480px]">

            {/* Copy */}
            <div>
              <p className="text-[11px] font-black uppercase tracking-[0.2em] text-brand-500 mb-5">LocalContact</p>
              <h1 className="text-[2.6rem] xl:text-[3.1rem] font-black leading-[1.08] tracking-[-0.03em] text-white mb-5">
                {mode === 'login' ? (
                  <>Good to have<br />you back.</>
                ) : (
                  <>Find the person<br />who <span className="text-brand-500">actually decides.</span></>
                )}
              </h1>
              <p className="text-[15px] text-slate-400 leading-relaxed max-w-[360px]">
                {mode === 'login'
                  ? 'Your contacts and search history are waiting. Pick up right where you left off.'
                  : 'Enter any local business address and get the name and title of the owner or manager, instantly.'}
              </p>
            </div>

            {/* Product preview card */}
            <div className="bg-white/[0.04] border border-white/[0.08] rounded-2xl overflow-hidden shadow-2xl">

              {/* Search bar mock */}
              <div className="px-5 py-4 border-b border-white/[0.06] flex items-center gap-3">
                <div className="flex-1 bg-white/[0.06] rounded-lg px-3.5 py-2 flex items-center gap-2.5">
                  <svg className="w-3.5 h-3.5 text-slate-500 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                  <span className="text-[12px] text-slate-500 font-medium">The Coffee House, 42 Baker Street, London</span>
                </div>
                <div className="w-7 h-7 rounded-lg bg-brand-500 flex items-center justify-center shrink-0">
                  <svg className="w-3.5 h-3.5 text-slate-900" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                  </svg>
                </div>
              </div>

              {/* Company header */}
              <div className="px-5 py-3.5 border-b border-white/[0.06] flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                  <div className="w-7 h-7 rounded-lg bg-slate-700 flex items-center justify-center">
                    <svg className="w-3.5 h-3.5 text-slate-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 21v-7.5a.75.75 0 01.75-.75h3a.75.75 0 01.75.75V21m-4.5 0H2.36m11.14 0H18m0 0h3.64m-1.39 0V9.349m-16.5 11.65V9.35m0 0a3.001 3.001 0 003.75-.615A2.993 2.993 0 009.75 9.75c.896 0 1.7-.393 2.25-1.016a2.993 2.993 0 002.25 1.016c.896 0 1.7-.393 2.25-1.016a3.001 3.001 0 003.75.614m-16.5 0a3.004 3.004 0 01-.621-4.72L4.318 3.44A1.5 1.5 0 015.378 3h13.243a1.5 1.5 0 011.06.44l1.19 1.189a3 3 0 01-.621 4.72m-13.5 8.65h3.75a.75.75 0 00.75-.75V13.5a.75.75 0 00-.75-.75H6.75a.75.75 0 00-.75.75v3.75c0 .415.336.75.75.75z" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-[13px] font-bold text-white leading-tight">The Coffee House</p>
                    <p className="text-[11px] text-slate-500">London, UK</p>
                  </div>
                </div>
                <span className="text-[10px] font-black bg-brand-500/15 text-brand-400 px-2.5 py-1 rounded-full">3 found</span>
              </div>

              {/* Contacts */}
              <div className="divide-y divide-white/[0.05]">
                {CONTACTS.map((c) => (
                  <div
                    key={c.initials}
                    className="px-5 py-3 flex items-center gap-3"
                    style={{ animation: `fadeSlideIn 0.4s ease ${c.delay} both` }}
                  >
                    <div className="w-8 h-8 rounded-full bg-slate-700 border border-slate-600 text-slate-300 text-[10px] font-black flex items-center justify-center shrink-0">
                      {c.initials}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[13px] font-semibold text-slate-200 leading-tight">{c.name}</p>
                      <p className="text-[11px] text-slate-500">{c.title}</p>
                    </div>
                    <svg className="w-3.5 h-3.5 text-slate-700" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                ))}
              </div>

              {/* Footer */}
              <div className="px-5 py-3 bg-white/[0.02] flex items-center justify-between">
                <span className="text-[11px] text-slate-600 flex items-center gap-1.5">
                  <svg className="w-3 h-3 text-brand-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 13.5l10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75z" />
                  </svg>
                  1 credit used
                </span>
                <span className="text-[11px] text-brand-400 font-bold">Export CSV</span>
              </div>
            </div>

            {/* Trust line */}
            <p className="text-[12px] text-slate-600 leading-relaxed">
              GDPR compliant &nbsp;·&nbsp; 30-day auto data deletion &nbsp;·&nbsp; No subscription required
            </p>
          </div>

          {/* ── Right: form card ── */}
          <div className="w-full max-w-[420px] lg:max-w-[400px] xl:max-w-[420px]">
            <div className="bg-white rounded-3xl shadow-2xl shadow-black/40 p-8 sm:p-10">

              {/* Mobile logo */}
              <div className="flex lg:hidden mb-7">
                <img src={logo} alt="LocalContact" className="h-7 w-auto object-contain" />
              </div>

              {/* Title */}
              <div className="mb-7">
                <h2 className="text-[26px] font-extrabold text-slate-900 tracking-tight mb-1.5 leading-tight">{title}</h2>
                {subtitle && <p className="text-[14px] text-slate-500 leading-relaxed">{subtitle}</p>}
              </div>

              {children}
            </div>

            {/* Below-card note */}
            <p className="text-center text-[12px] text-slate-600 mt-5">
              {mode === 'login' ? (
                <>No account?{' '}
                  <Link to="/register" className="text-brand-400 font-bold hover:text-brand-300 transition-colors">Create one free</Link>
                </>
              ) : (
                <>Have an account?{' '}
                  <Link to="/login" className="text-brand-400 font-bold hover:text-brand-300 transition-colors">Sign in</Link>
                </>
              )}
            </p>
          </div>

        </div>
      </div>

      <style>{`
        @keyframes fadeSlideIn {
          from { opacity: 0; transform: translateX(-8px); }
          to   { opacity: 1; transform: translateX(0); }
        }
      `}</style>
    </div>
  );
}
